import { Award, Users, Target, Lightbulb, Building2, Globe } from 'lucide-react';
import { useSiteContent } from '../hooks/useSiteContent';

interface HighlightItem {
  icon: string;
  title: string;
  description: string;
}

interface StatItem {
  value: string;
  label: string;
}

interface AboutContent {
  company_name: string;
  tagline: string;
  description: string;
  highlights: HighlightItem[];
  stats: StatItem[];
}

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  Building2,
  Target,
  Users,
  Award,
};

const defaultContent: AboutContent = {
  company_name: '郑州博乐信息技术有限公司',
  tagline: '用科技赋能物流，让大宗物资运输更智能、更高效',
  description: '郑州博乐信息技术有限公司创立于2007年，是一家专注于企业数智化供应链物流管理方案的高新技术企业。当前业务已覆盖化工、水泥、矿山、粮食、建筑、煤炭、制造、焦化、食品、电厂、有色金属等10余行业。',
  highlights: [
    {
      icon: 'Building2',
      title: '企业定位',
      description: '专注于企业数智化供应链物流管理，是国内领先的企业物流数智化解决方案提供商',
    },
    {
      icon: 'Target',
      title: '核心业务',
      description: '一码通管理系统、运输管理（TMS）、 无人值守称重管理、车辆智能排队管理、智能鹤管装车、危化品停车场管理、颗粒物/散物自动定量装车、智能调度管理',
    },
    {
      icon: 'Users',
      title: '服务保障',
      description: '提供管理咨询、方案设计、系统建设、运营制度优化、现场实施、售后运维一站式服务',
    },
    {
      icon: 'Award',
      title: '技术实力',
      description: '拥有自主知识产权的物流管理平台，获得多项软件著作权和技术专利',
    },
  ],
  stats: [
    { value: '19+', label: '年行业经验' },
    { value: '1000+家', label: '服务企业' },
    { value: '150+', label: '覆盖城市' },
    { value: '99.9%', label: '系统稳定性' },
  ],
};

export default function About() {
  const { content } = useSiteContent('about');
  const data: AboutContent = content?.content || defaultContent;

  const getIcon = (iconName: string) => {
    return iconMap[iconName] || Building2;
  };

  return (
    <section id="about" className="section-padding bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-ocean-50 rounded-full border border-ocean-100 mb-4">
            <span className="w-2 h-2 bg-ocean-500 rounded-full" />
            <span className="text-ocean-600 text-sm font-medium">关于我们</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
            {data.company_name}
          </h2>
          <p className="text-lg text-slate-600">
            {data.tagline}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          <div className="space-y-6">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-slate-100 rounded-lg">
              <Globe className="w-4 h-4 text-ocean-600" />
              <span className="text-slate-700 text-sm font-medium">BLSOFT 博乐软件</span>
            </div>
            
            <h3 className="text-2xl font-bold text-slate-900">
              企业数智化供应链物流 · 解决方案服务商
            </h3>
            
            <p className="text-slate-600 leading-relaxed">
              {data.description}
            </p>
            
            <p className="text-slate-600 leading-relaxed">
              公司自主研发的企业智能物流一卡通管理、无人值守称重系统、智能自动装车系统、智能排队调度系统等产品，已在全国150+城市、1000多家企业成功应用，帮助企业实现数字化转型，提升运营效率，降低管理成本。
            </p>

            <div className="flex flex-wrap gap-4 pt-4">
              <div className="flex items-center gap-2 text-slate-700">
                <Lightbulb className="w-5 h-5 text-ocean-500" />
                <span className="font-medium">持续创新</span>
              </div>
              <div className="flex items-center gap-2 text-slate-700">
                <Target className="w-5 h-5 text-ocean-500" />
                <span className="font-medium">客户至上</span>
              </div>
              <div className="flex items-center gap-2 text-slate-700">
                <Award className="w-5 h-5 text-ocean-500" />
                <span className="font-medium">品质保证</span>
              </div>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            {(data.highlights || defaultContent.highlights).map((item: HighlightItem) => {
              const IconComponent = getIcon(item.icon);
              return (
                <div
                  key={item.title}
                  className="p-6 bg-slate-50 rounded-xl hover:bg-ocean-50 hover:shadow-lg transition-all duration-300"
                >
                  <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center mb-4 shadow-sm">
                    <IconComponent className="w-6 h-6 text-ocean-600" />
                  </div>
                  <h4 className="font-semibold text-slate-900 mb-2">{item.title}</h4>
                  <p className="text-sm text-slate-600 leading-relaxed">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>

        <div className="bg-gradient-to-r from-ocean-500 to-primary-600 rounded-2xl p-8 lg:p-12">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {(data.stats || defaultContent.stats).map((stat: StatItem) => (
              <div key={stat.label} className="text-center">
                <div className="text-3xl lg:text-4xl font-bold text-white mb-2">{stat.value}</div>
                <div className="text-ocean-100 text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-16 text-center">
          <p className="text-slate-600 mb-4">了解更多博乐信息，欢迎访问官网</p>
          <a
            href="https://www.bydzxyun.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 py-3 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors"
          >
            <Globe className="w-4 h-4" />
            <span>访问博乐官网</span>
          </a>
        </div>
      </div>
    </section>
  );
}
